import { BadgeImage } from "@/components/badges/badge-image"
import { PremiumBadge } from "@/components/badges/premium-badge"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import type { MedallionViewModel } from "@/lib/badge-medallions"
import { cn } from "@/lib/utils"

/**
 * OKS-969 — one medallion: artwork + tier treatment, plus a `×N` chip when the
 * family repeats. No visible name, no rarity pill, no date (BRIEF D2).
 *
 * A medallion with a visible name would be a tile, and the tile already exists
 * (`<BadgeTile>` in `<BadgeGrid>`, clamp-hardened by OKS-926). The nameless
 * strip is the thing that did not exist, so it is the only thing built here.
 *
 * # It composes no copy (the OKS-937 rule)
 *
 * There is no `useTranslation` in this file. `ariaLabel`, `countChipLabel` and
 * `popover` all arrive already translated from the call site. A component that
 * composes user-facing copy without i18n produces untranslated strings *by
 * construction* — which is how a hardcoded English `— locked` survived three
 * tickets in a French-default product. `ariaLabel` is REQUIRED, so a call site
 * that forgets to announce is a `tsc` failure rather than a runtime guess.
 *
 * # The `×N` chip is `aria-hidden`, and the count is in the accessible name
 *
 * The chip is the whole point of the rollup, so a screen-reader user must get
 * the count too — and a bare glyph gives them nothing. The caller passes an
 * `ariaLabel` built from the count-bearing key for repeated families, so the
 * count reaches assistive tech through the accessible name and the chip stays
 * decorative. Announcing both would say "3" twice.
 */
interface BadgeMedallionProps {
  vm: MedallionViewModel
  /** `preview` 48px (brand detail) | `corner` 28px (OKS-970 roster density). */
  size: "preview" | "corner"
  /** REQUIRED — the complete accessible name, already translated (BRIEF AC8). */
  ariaLabel: string
  /** Already-translated `×N`. Omit for a single-award family (no chip). */
  countChipLabel?: string
  /**
   * Already-translated popover content. Omit to render an inert medallion —
   * that is how the strip's `interactive={false}` reaches here, and it keeps
   * OKS-970's "the roster cell does not navigate" posture available.
   */
  popover?: { title: string; lines: string[] }
  /**
   * OKS-975 — already-translated family name, surfaced as a native hover
   * tooltip on the INERT medallion only (BRIEF D2 / AC9). Optional: supplying
   * it is the call site's product decision, exactly like the strip's `hint`.
   *
   * ⚠️ Placement is an accessibility constraint, not a detail. It must NOT
   * land on the `role="img"` node below, which already carries a complete
   * count-bearing `aria-label` — several screen readers treat `title` on a
   * named node as an accessible DESCRIPTION and would announce the badge
   * twice. It goes on the inner wrapper, whose contents are already excluded
   * from the accessibility tree by `role="img"`. Pointer users gain a
   * tooltip; screen-reader output is byte-identical to before.
   *
   * Rendered on the inert branch only, and that is enforced HERE rather than
   * at the call site: a future interactive surface that supplies one must not
   * stack a tooltip on top of a popover.
   */
  hoverName?: string
  className?: string
}

export function BadgeMedallion({
  vm,
  size,
  ariaLabel,
  countChipLabel,
  popover,
  hoverName,
  className,
}: BadgeMedallionProps) {
  const artwork = vm.showImage ? (
    <BadgeImage
      imageUrl={vm.imageUrl}
      accentColor={vm.accentColor}
      // OKS-975 — the ONLY call site that opts into the glyph. `vm.icon` was
      // already in scope on this expression and was being dropped: the arm one
      // line down has always drawn it, so an imageless custom badge was the
      // single case in the product that rendered a blank coloured square. It
      // is ignored when `imageUrl` is present (artwork wins), so this is inert
      // for every custom badge that has real art.
      icon={vm.icon}
      name=""
      state="earned"
      size={size}
      decorative
    />
  ) : (
    <PremiumBadge tier={vm.tier} icon={vm.icon} state="earned" size={size} />
  )

  const body = (
    <>
      {artwork}
      {countChipLabel && (
        <span
          data-testid="badge-medallion-count"
          // Decorative: the count is already in `ariaLabel`. See the header.
          aria-hidden="true"
          className={cn(
            "bg-foreground text-background absolute -right-1 -bottom-1 rounded-full font-semibold tabular-nums",
            size === "corner"
              ? "px-1 py-0 text-[9px] leading-[1.1]"
              : "px-1.5 py-0.5 text-[10px] leading-none",
          )}
        >
          {countChipLabel}
        </span>
      )}
    </>
  )

  const shell = cn("relative inline-flex shrink-0", className)

  if (!popover) {
    return (
      <span
        data-testid="badge-medallion"
        data-identifier={vm.identifier}
        data-tier={vm.tier}
        data-count={vm.count}
        data-interactive="false"
        // `img` (not `group`): an inert medallion is a single graphic with a
        // name and no interactive or structural content inside it.
        role="img"
        aria-label={ariaLabel}
        className={shell}
      >
        {/*
          OKS-975 — the `title` lives on this INNER node, never on the
          `role="img"` above (see the prop's doc). `role="img"` makes its
          descendants presentational, so this node is invisible to assistive
          tech while still being a real box the browser will hang a native
          tooltip off. `relative inline-flex` reproduces the shell's own
          positioning context so the absolutely-placed `×N` chip lands exactly
          where it did before.

          Only wrapped when there IS a hover name: without one the markup is
          byte-identical to pre-OKS-975, so no inert call site that opts out
          pays a DOM change.
        */}
        {hoverName ? (
          <span
            data-testid="badge-medallion-hover-name"
            title={hoverName}
            className="relative inline-flex"
          >
            {body}
          </span>
        ) : (
          body
        )}
      </span>
    )
  }

  return (
    <Popover>
      <PopoverTrigger
        data-testid="badge-medallion"
        data-identifier={vm.identifier}
        data-tier={vm.tier}
        data-count={vm.count}
        data-interactive="true"
        aria-label={ariaLabel}
        className={cn(
          shell,
          "focus-visible:ring-ring cursor-pointer rounded-full transition hover:scale-105 focus-visible:ring-2 focus-visible:outline-none",
        )}
      >
        {body}
      </PopoverTrigger>
      <PopoverContent
        data-testid="badge-medallion-popover"
        className="flex w-64 flex-col gap-1"
      >
        <p className="text-sm font-semibold">{popover.title}</p>
        {/*
          One line per underlying award, newest first. For a repeated family
          these are the individual wins (each month, each mission title) — the
          disambiguation the medallion itself deliberately does not carry.
        */}
        <ul className="text-muted-foreground flex flex-col gap-0.5 text-xs">
          {popover.lines.map((line, index) => (
            // Index key: `lines` is a positional projection of `vm.awards`,
            // which is server-ordered and never reordered or filtered here, so
            // position is stable for this medallion's lifetime. The lines
            // themselves are not unique (repeat wins can render identical text
            // when a disambiguator is missing), so they cannot key.
            <li key={index}>{line}</li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
